import { useState, useCallback } from 'react';
import { SORT_BY_VALUES, SORT_ORDER_VALUES, SortConfig } from '../types';

export const DEFAULT_SORT: SortConfig[] = [
  { sortBy: SORT_BY_VALUES.CREATION_DATE, order: SORT_ORDER_VALUES.DESC },
  { sortBy: SORT_BY_VALUES.NAME, order: SORT_ORDER_VALUES.ASC }
];

const readSortFromStorage = (storageKey: string): SortConfig[] => {
  const key = `${storageKey}-sort-params`;
  const stored = sessionStorage.getItem(key);
  if (!stored) {
    // Initialize with defaults if not present
    sessionStorage.setItem(key, JSON.stringify(DEFAULT_SORT));
    return DEFAULT_SORT;
  }

  try {
    const params = JSON.parse(stored);

    // If it's already an array, validate it
    if (Array.isArray(params)) {
      const isValid = params.every(
        (sort: any) =>
          sort.sortBy &&
          sort.order &&
          Object.values(SORT_BY_VALUES).includes(sort.sortBy) &&
          Object.values(SORT_ORDER_VALUES).includes(sort.order)
      );
      if (isValid && params.length > 0) {
        return params;
      }
    }
    // If validation fails, use defaults
    sessionStorage.setItem(key, JSON.stringify(DEFAULT_SORT));
    return DEFAULT_SORT;
  } catch (e) {
    // If parsing fails, use defaults
    // eslint-disable-next-line no-console
    console.error('Failed to parse sort params from sessionStorage', e);
    sessionStorage.setItem(key, JSON.stringify(DEFAULT_SORT));
    return DEFAULT_SORT;
  }
};

export const useOperationSort = (storageKey: string) => {
  const searchParams = new URLSearchParams(globalThis.window.location.search);
  const sortParamsFromURL = searchParams.get('sorts');
  let initialParams: SortConfig[];

  if (sortParamsFromURL) {
    try {
      const parsed = JSON.parse(sortParamsFromURL);
      if (Array.isArray(parsed)) {
        initialParams = parsed;
      } else {
        initialParams = readSortFromStorage(storageKey);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to parse sort params from URL', e);
      initialParams = readSortFromStorage(storageKey);
    }
  } else {
    initialParams = readSortFromStorage(storageKey);
  }

  const [sortParams, setSortParams] = useState<SortConfig[]>(initialParams);

  const setSort = useCallback(
    (sorts: SortConfig[]) => {
      sessionStorage.setItem(
        `${storageKey}-sort-params`,
        JSON.stringify(sorts)
      );
      setSortParams(sorts);
    },
    [storageKey]
  );

  return {
    sorts: sortParams,
    setSort
  };
};

export default useOperationSort;
